export const MSI_CUSTOMER_VERSION = "MSI_CUSTOMER_1.2";
export const MSI_CUSTOMER_COMPATIBLE_VERSIONS = new Set(["MSI_CUSTOMER_1.0", "MSI_CUSTOMER_1.1", MSI_CUSTOMER_VERSION]);
export const MSI_CUSTOMER_URL = "/data/msi-customer.json";
export const MSI_CUSTOMER_TIMEOUT_MS = 8000;
const TOP_LEVEL = ["contract_version", "freshness_status", "generated_at", "latest_completed_session", "market_close", "market_environment", "market_session_date", "generation_mode", "run_id", "session_state", "source_as_of", "structures"];
const ENV_FIELDS = ["core_index_breadth", "health_method", "health_score", "missing_core_indexes", "risk_state", "vix_regime", "vix_state"];
const BREADTH_FIELDS = ["down", "up", "up_pct", "valid", "wait"];
const ROW_FIELDS = ["as_of", "asset_name", "sector", "current_price", "customer_reason", "detail", "direction", "msi_score", "structure_change_condition", "structure_id", "structure_quality_label", "structure_state", "ticker"];
const ROW_FIELDS_V12 = [...ROW_FIELDS, "exchange", "confidence_score", "daily_20ma_distance_pct", "daily_change_amount", "daily_change_pct"];
const DIRECTIONS = new Set(["UP", "WAIT", "DOWN"]);
const BASE_TOP_LEVEL = ["contract_version", "freshness_status", "generated_at", "market_environment", "source_as_of", "structures"];

const exactKeys = (value, allowed) => value && typeof value === "object"
  && Object.keys(value).every((key) => allowed.includes(key))
  && allowed.every((key) => Object.prototype.hasOwnProperty.call(value, key));

export function validateMsiCustomerContract(value) {
  if (!MSI_CUSTOMER_COMPATIBLE_VERSIONS.has(value.contract_version)) throw new Error("UNSUPPORTED_MSI_CUSTOMER_CONTRACT");
  const allowedTop = value.contract_version === "MSI_CUSTOMER_1.0" ? BASE_TOP_LEVEL : TOP_LEVEL;
  if (!value || typeof value !== "object" || Object.keys(value).some((key) => !allowedTop.includes(key))
    || BASE_TOP_LEVEL.some((key) => !Object.prototype.hasOwnProperty.call(value, key))) throw new Error("INVALID_MSI_CUSTOMER_SCHEMA");
  if (value.contract_version !== "MSI_CUSTOMER_1.0") {
    if (value.session_state !== undefined && !["PREMARKET", "OPEN", "AFTER_HOURS", "CLOSED"].includes(value.session_state)) throw new Error("INVALID_MSI_SESSION_STATE");
    if (value.generation_mode !== undefined && !["INTRADAY", "CLOSED"].includes(value.generation_mode)) throw new Error("INVALID_MSI_GENERATION_MODE");
    if ((value.session_state === "OPEN") !== (value.generation_mode === "INTRADAY")) throw new Error("CONTRADICTORY_MSI_SESSION_METADATA");
    if (value.run_id !== undefined && !value.run_id) throw new Error("INVALID_MSI_RUN_ID");
  }
  if (!exactKeys(value.market_environment, ENV_FIELDS)) throw new Error("INVALID_MARKET_ENVIRONMENT");
  if (!exactKeys(value.market_environment.core_index_breadth, BREADTH_FIELDS)) throw new Error("INVALID_CORE_INDEX_BREADTH");
  const health = value.market_environment.health_score;
  if (health !== null && (!Number.isFinite(Number(health)) || Number(health) < 0 || Number(health) > 100)) throw new Error("INVALID_MARKET_HEALTH");
  if (!Array.isArray(value.structures)) throw new Error("INVALID_MSI_STRUCTURES");
  value.structures.forEach((row) => {
    const rowFields = value.contract_version === MSI_CUSTOMER_VERSION ? ROW_FIELDS_V12 : ROW_FIELDS;
    if (!row || typeof row !== "object" || Object.keys(row).some((key) => !rowFields.includes(key))) {
      throw new Error("PRIVATE_OR_UNEXPECTED_STRUCTURE_FIELD");
    }
    for (const key of ["structure_id", "ticker", "direction", "structure_state", "as_of"]) {
      if (!row[key]) throw new Error(`MISSING_STRUCTURE_${key.toUpperCase()}`);
    }
    if (value.contract_version === MSI_CUSTOMER_VERSION && ["asset_name", "exchange", "current_price", "confidence_score", "daily_20ma_distance_pct", "daily_change_amount", "daily_change_pct"].some((key) => !Object.prototype.hasOwnProperty.call(row, key))) throw new Error("MISSING_MSI_CUSTOMER_1_2_FIELD");
    if (!DIRECTIONS.has(row.direction)) throw new Error("INVALID_CUSTOMER_DIRECTION");
    if (row.customer_reason !== undefined && !Array.isArray(row.customer_reason)) throw new Error("INVALID_CUSTOMER_REASON");
    if (row.msi_score !== undefined && (!Number.isFinite(Number(row.msi_score)) || Number(row.msi_score) < 0 || Number(row.msi_score) > 100)) throw new Error("INVALID_STRUCTURE_SCORE");
    if (row.current_price !== undefined && row.current_price !== null && (!Number.isFinite(Number(row.current_price)) || Number(row.current_price) < 0)) throw new Error("INVALID_CURRENT_PRICE");
    if (value.contract_version === MSI_CUSTOMER_VERSION) {
      if (row.confidence_score !== null && row.confidence_score !== undefined && (!Number.isFinite(Number(row.confidence_score)) || Number(row.confidence_score) < 0 || Number(row.confidence_score) > 100)) throw new Error("INVALID_CONFIDENCE_SCORE");
      if (row.daily_20ma_distance_pct !== null && row.daily_20ma_distance_pct !== undefined && !Number.isFinite(Number(row.daily_20ma_distance_pct))) throw new Error("INVALID_DAILY_20MA_DISTANCE");
      const amountAvailable = row.daily_change_amount !== null && row.daily_change_amount !== undefined;
      const percentAvailable = row.daily_change_pct !== null && row.daily_change_pct !== undefined;
      if (amountAvailable !== percentAvailable) throw new Error("INVALID_DAILY_MOVEMENT_PAIR");
      if (amountAvailable && (!Number.isFinite(Number(row.daily_change_amount)) || !Number.isFinite(Number(row.daily_change_pct)) || row.current_price == null)) throw new Error("INVALID_DAILY_MOVEMENT");
      validateDetail(row.detail, row.current_price, true);
    } else if (value.contract_version === "MSI_CUSTOMER_1.1") validateDetail(row.detail, row.current_price, false);
  });
  return value;
}

const exactOrSubset = (value, allowed, exact = false) => value && typeof value === "object" && !Array.isArray(value)
  && Object.keys(value).every((key) => allowed.includes(key)) && (!exact || allowed.every((key) => Object.prototype.hasOwnProperty.call(value, key)));
const listOf = (value, allowed) => Array.isArray(value) && value.every((item) => exactOrSubset(item, allowed));
const DETAIL = ["summary", "customer_reasons", "guru_msi", "chart"];
const GURU = ["timeframes", "moving_averages", "bollinger_bands", "key_levels", "confirmation", "conflicts"];
function validateDetail(detail, currentPrice, v12) {
  if (!exactOrSubset(detail, DETAIL, true)) throw new Error("INVALID_MSI_DETAIL");
  if (!exactOrSubset(detail.summary, ["direction", "structure_quality_label", "structure_state", "as_of"], true)) throw new Error("INVALID_MSI_DETAIL_SUMMARY");
  if (!Array.isArray(detail.customer_reasons) || detail.customer_reasons.length > 3 || !listOf(detail.customer_reasons, ["category", "summary"])) throw new Error("INVALID_MSI_CUSTOMER_REASONS");
  const guru = detail.guru_msi;
  if (!exactOrSubset(guru, GURU, true)) throw new Error("INVALID_GURU_MSI");
  const timeframeFields = ["timeframe", "direction", "structure_state", "confirmation_state", "candle_state", "candle_period_start", "candle_expected_close", "candle_status", "candle_age_seconds", "chart_state", "as_of", ...(v12 ? ["stoch_rsi_state", "macd_state", "volume_state"] : [])];
  if (!listOf(guru.timeframes, timeframeFields)) throw new Error("INVALID_GURU_TIMEFRAMES");
  if (!listOf(guru.moving_averages, ["timeframe", "period", "average_type", "value", "price_relation", "structure_action", "as_of"])) throw new Error("INVALID_GURU_MOVING_AVERAGES");
  if (!listOf(guru.bollinger_bands, ["timeframe", "upper", "middle", "lower", "price_position", "band_state", "as_of"])) throw new Error("INVALID_GURU_BOLLINGER");
  const levelFields = ["level_type", "label", "value", "timeframe", "directional_role", "as_of", ...(v12 ? ["sentinel_state"] : [])];
  if (!listOf(guru.key_levels, levelFields)) throw new Error("INVALID_GURU_LEVELS");
  if (v12) guru.key_levels.forEach((level) => {
    if (level.sentinel_state != null && level.value != null) throw new Error("INVALID_SENTINEL_LEVEL");
    if (level.value != null && Number.isFinite(Number(currentPrice)) && level.directional_role === "UPSIDE" && Number(level.value) <= Number(currentPrice)) throw new Error("INVALID_UPSIDE_LEVEL");
    if (level.value != null && Number.isFinite(Number(currentPrice)) && level.directional_role === "RISK" && Number(level.value) >= Number(currentPrice)) throw new Error("INVALID_RISK_LEVEL");
  });
  if (!exactOrSubset(guru.confirmation, ["confluence_score", "timeframe_alignment", "candle_state", "chart_state", "market_environment_alignment"])) throw new Error("INVALID_GURU_CONFIRMATION");
  if (!Array.isArray(guru.conflicts)) throw new Error("INVALID_GURU_CONFLICTS");
  if (detail.chart !== null && (!exactOrSubset(detail.chart, ["provider", "symbol", "url"], true) || detail.chart.provider !== "TRADINGVIEW" || !detail.chart.symbol.includes(":"))) throw new Error("INVALID_MSI_CHART");
}

export const isCurrentMsiContract = (contract) => contract?.freshness_status === "CURRENT"
  && contract?.market_environment?.health_score !== null
  && contract?.market_environment?.vix_regime !== "UNKNOWN"
  && contract?.market_environment?.vix_state !== "UNKNOWN";

export async function fetchMsiCustomerContract(fetchImpl = fetch, { timeoutMs = MSI_CUSTOMER_TIMEOUT_MS } = {}) {
  const controller = typeof AbortController === "undefined" ? null : new AbortController();
  let timeout;
  const timeoutFailure = new Promise((_, reject) => {
    timeout = setTimeout(() => {
      controller?.abort();
      reject(new Error("MSI_CUSTOMER_TIMEOUT"));
    }, timeoutMs);
  });

  try {
    const response = await Promise.race([
      fetchImpl(MSI_CUSTOMER_URL, {
        cache: "no-store",
        credentials: "same-origin",
        ...(controller ? { signal: controller.signal } : {}),
      }),
      timeoutFailure,
    ]);
    if (!response?.ok || response.status !== 200) {
      throw new Error(`MSI_CUSTOMER_UNAVAILABLE_${response?.status || "NO_RESPONSE"}`);
    }
    const contentType = response.headers?.get?.("content-type") || "";
    if (!/(?:application|text)\/(?:[a-z0-9.+-]*\+)?json\b/i.test(contentType)) {
      throw new Error("MSI_CUSTOMER_INVALID_CONTENT_TYPE");
    }
    const body = await response.text();
    if (!body.trim()) throw new Error("MSI_CUSTOMER_EMPTY_BODY");
    let parsed;
    try {
      parsed = JSON.parse(body);
    } catch {
      throw new Error("MSI_CUSTOMER_INVALID_JSON");
    }
    return validateMsiCustomerContract(parsed);
  } finally {
    clearTimeout(timeout);
  }
}

export async function loadMsiCustomerState(fetchImpl = fetch, options = {}) {
  try {
    const contract = await fetchMsiCustomerContract(fetchImpl, options);
    const current = isCurrentMsiContract(contract);
    const previewStale = options.allowPreviewStale === true
      && contract?.freshness_status === "STALE";
    return current || previewStale
      ? { status: "ready", contract }
      : { status: "stale", contract };
  } catch (error) {
    console.error("MSI customer contract unavailable", error?.message || "UNKNOWN");
    return { status: "unavailable", contract: null };
  }
}

export const filterStructures = (structures, direction) => direction === "ALL"
  ? structures
  : structures.filter((row) => row.direction === direction);
